import { formatDate } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { ToastService } from 'src/app/shared/services/toast.service';
import { CustomerInfo } from '../../models/customer-info';
import { CustomerAccountRecord } from '../../models/customer-account';
import { ProjectStatus, TeamAssist } from '../../models/org-status';
import { IMToolService } from '../../services/im-tool.service';
import { AlertWizardComponent } from '../alert-wizard/alert-wizard.component';
import * as constants from 'src/app/config/app-constants';
import { Router } from '@angular/router';
import { ImportWizardComponent } from '../import-wizard/import-wizard.component';
import { FormControl } from '@angular/forms';
import { DropDownTwo } from 'src/app/internal-user/drug-management/models/dropdown';
import { debounce, distinctUntilChanged, of, Subject, Subscription, timer } from 'rxjs';
import { Sort } from '@angular/material/sort';
import { finalize } from 'rxjs/operators';

interface ImplementationRecord extends CustomerInfo {
  caseId?: string;
  salesforceStatus?: string;
  targetInstallDateDisplay: string;
  selectionCustomerKey: string;
  selectionDeviceTypeKey: string | null;
}

interface CustomerAccountGroup {
  key: string;
  customerName: string;
  customerNumber: string;
  records: ImplementationRecord[];
  caseCount: number;
  deviceCount: number;
  latestRecord: ImplementationRecord;
  expanded: boolean;
}

interface LandingRow {
  type: 'account' | 'implementation';
  account: CustomerAccountGroup;
  record?: ImplementationRecord;
}

@Component({
    selector: "app-multi-max-device-customer-management",
    templateUrl: "./multi-max-device-customer-management.component.html",
    styleUrls: ["./multi-max-device-customer-management.component.scss"],
    standalone: false
})
export class MultiMaxDeviceCustomerManagementComponent implements OnInit, OnDestroy {
  private customerDataSubscription?: Subscription;
  private readonly searchChanges = new Subject<{ value: string; criteria: string }>();
  private searchSubscription?: Subscription;
  private readonly accountRows = new WeakMap<CustomerAccountGroup, LandingRow>();
  private readonly implementationRows = new WeakMap<ImplementationRecord, LandingRow>();
  private readonly selectedImplementationIds = new Set<string>();
  private selectedCustomerKey: string | null = null;
  private selectedDeviceTypeKey: string | null = null;
  accounts: CustomerAccountGroup[] = [];
  dataSource = new MatTableDataSource<LandingRow>();
  @ViewChild(MatSort, { static: false }) sort!: MatSort;

  displayedColumns: string[] = [
    "expand",
    "customerName",
    "caseCount",
    "subDeviceTypeName",
    "lastEdited",
    "statusDescription",
    "salesforceStatus",
    "targetInstallDate",
    "owner",
    "action",
  ];
  projectStatus: ProjectStatus[] = [];
  teamAssists: TeamAssist[] = [];
  salesforceStatusList:DropDownTwo[]= [];
  salesforceStatus:FormControl = new FormControl('');
  selected: string = "1";
  selectedProjectStatus : number = 0;
  selectedTeamAssists: number = 1;
  selectedProduct : string = 'All';
  searchCriteria = [
    { value: 'customerName', label: 'Customer Name' },
    { value: 'caseNumber', label: 'Case Number', pattern: /^\d+$/, errorMsg: 'Case Number must contain only numbers' },
    { value: 'customerNumber', label: 'Customer Number', pattern: /^\d+$/, errorMsg: 'Customer Number must contain only numbers' },
    { value: 'assetNumber', label: 'Asset Number', pattern: /^\d+$/, errorMsg: 'Asset Number must contain only numbers' },
  ];
  selectedSearchCriteria = this.searchCriteria[0];
  validationError = '';
  //#region Pagination properties
  totalRows: number = 0;
  currentPage: number = 0;
  pageSize: number = 25;
  pageSizeOptions: number[] = [10, 25, 50];
  get visibleEnd(): number {
    return Math.min((this.currentPage + 1) * this.pageSize, this.totalRows);
  }
  get hasSelectedImplementations(): boolean {
    return this.selectedImplementationIds.size > 0;
  }
  get totalPages(): number {
    return Math.ceil(this.totalRows / this.pageSize);
  }
  get pageNumbers(): number[] {
    const pageWindowSize = 5;
    const firstPage = Math.max(
      0,
      Math.min(this.currentPage - 2, this.totalPages - pageWindowSize)
    );
    const lastPage = Math.min(firstPage + pageWindowSize, this.totalPages);
    return Array.from({ length: lastPage - firstPage }, (_, index) => firstPage + index);
  }

  Products = ['MAX', 'Pass', 'ATP', 'Mini', 'Synmed', 'ATP(CAPS)', 'Legacy Connect', 'All'];
  setPageSize(value: string): void {
    this.pageSize = Number(value);
    this.currentPage = 0;
    this.getCustomerData();
  }

  setPage(pageIndex: number): void {
    if (pageIndex < 0 || pageIndex >= this.totalPages || pageIndex === this.currentPage) {
      return;
    }
    this.currentPage = pageIndex;
    this.getCustomerData();
  }
  //#endregion

  //#region Sorting Properties
    sortDir:string="asc";
    sortColumnName:string="customerName";
    sortChange(event: Sort) {
      this.sortColumnName = event.direction ? event.active : 'customerName';
      this.sortDir = event.direction || 'asc';
      this.currentPage = 0;
      this.getCustomerData();
    }
    searchText: string="";
    statusId:number=0;
  //#endregion


  @HostListener("window:resize")
  onResize() {
    if (window.innerWidth > 900) {
    }
  }
  constructor(
    private dialog: MatDialog,
    private imtoolService: IMToolService,
    private ngxLoader: NgxUiLoaderService,
    private toastService: ToastService,
    private route: Router
  ) {}

  ngOnInit(): void {
    this.searchSubscription = this.searchChanges.pipe(
      distinctUntilChanged((previous, current) =>
        previous.value === current.value && previous.criteria === current.criteria
      ),
      debounce(search => search.value === '' ? of(0) : timer(300))
    ).subscribe(search => this.doFilter(search.value, true, this.selectedProduct));
    this.getCustomerData();
    this.getFileStatusData();
    this.getSalesForceStatus();
    this.teamAssists.push({
      id: 1,
      value: "Your Worklist",
    });


  }
  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
    this.searchChanges.complete();
    this.customerDataSubscription?.unsubscribe();
  }

  onSearchInput(value: string): void {
    this.searchText = value;
    this.validateSearchText();
    this.searchChanges.next({ value, criteria: this.selectedSearchCriteria.value });
  }

  onSearchCriteriaChange(value: string): void {
    this.selectedSearchCriteria = this.searchCriteria.find(criteria => criteria.value === value) ?? this.searchCriteria[0];
    this.validateSearchText();
    if (this.searchText.trim()) {
      this.searchChanges.next({ value: this.searchText, criteria: this.selectedSearchCriteria.value });
    }
  }

  private validateSearchText(): boolean {
    const value = this.searchText.trim();
    const criteria = this.selectedSearchCriteria;

    if (!value || !criteria.pattern || criteria.pattern.test(value)) {
      this.validationError = '';
      return true;
    }

    this.validationError = criteria.errorMsg ?? '';
    return false;
  }

  getCustomerData() {
    if (!this.validateSearchText()) {
      return;
    }

    const CUSTOMERDATA_KEY = 'Customerdatakey';
    this.customerDataSubscription?.unsubscribe();
    this.ngxLoader.start(CUSTOMERDATA_KEY);
    this.customerDataSubscription = this.imtoolService.getCustomerAccounts(
      this.searchText,
      this.currentPage + 1,
      this.pageSize,
      this.sortDir,
      this.sortColumnName,
      this.statusId,
      this.salesforceStatus.value?.toString() ?? '',
      this.selectedProduct ?? '',
      this.selectedSearchCriteria.value
    )
      .pipe(finalize(() => this.ngxLoader.stop(CUSTOMERDATA_KEY)))
      .subscribe(
        (result) => {
          if (!result.succeed) {
            this.toastService.openToast(result.message, constants.ToastPanelType.error, 2);
            return;
          }
          this.accounts = result.data.data.map((account) => ({
            ...account,
            records: account.records.map((record) => this.mapImplementationRecord(record, account.key)),
            latestRecord: this.mapImplementationRecord(account.latestRecord, account.key),
            expanded: false,
          }));
          this.totalRows = result.data.totalRows;
          this.currentPage = result.data.currentPage - 1;
          this.pageSize = result.data.pageSize;
          this.updateVisibleRows();
          this.selectedImplementationIds.clear();
          this.selectedCustomerKey = null;
          this.selectedDeviceTypeKey = null;
        },

        (error: HttpErrorResponse) => {
          console.log(error);
          this.toastService.openToast(
            error.error?.message ?? error.message,
            constants.ToastPanelType.error,
            2
          );
        }
      );
  }

  private mapImplementationRecord(record: CustomerAccountRecord, customerKey: string): ImplementationRecord {
    return {
      sl: record.sl,
      customerId: record.id,
      assetProductLine: record.assetProductLine,
      customerName: record.customerName,
      selectionCustomerKey: customerKey,
      selectionDeviceTypeKey: this.getDeviceTypeKey(record),
      caseId: record.caseId,
      caseNumber: record.caseNumber,
      caseDetailsLink: record.caseDetailsLink ?? '',
      customerNumber: record.customerNumber,
      assetNumber: record.assetNumber,
      deviceType: record.deviceType,
      lastEdited: record.modifiedDate,
      deviceId: record.deviceId,
      status: {
        id: record.status?.id ?? 0,
        value: record.status?.description ?? '',
      },
      subDeviceTypeName: this.getDeviceDisplayName(record),
      statusDescription: record.status?.description ?? '',
      salesforceStatus: String(record.salesforceStatus ?? record.salesForceStatus ?? ''),
      targetInstallDate: record.targetInstallDate,
      targetInstallDateDisplay: this.formatTargetInstallDate(record.targetInstallDate),
      owner: record.modifiedBy,
    };
  }

  private formatTargetInstallDate(value: string | null | undefined): string {
    if (!value?.trim()) {
      return '';
    }
    const trimmed = value.trim();
    // Preserve the requested legacy display of the backend's minimum-date sentinel.
    if (/^(?:0001-01-01|01[-/]01[-/]0001)(?:[T\s].*)?$/.test(trimmed)) {
      return '01/01/2001';
    }
    if (/^\d{1,2}\/\d{1,2}\/\d{4}(?:[T\s].*)?$/.test(trimmed)) {
      // Match the original DatePipe's native parsing of backend slash-formatted dates.
      const legacyDate = new Date(trimmed);
      if (!Number.isNaN(legacyDate.getTime())) {
        return formatDate(legacyDate, 'MM/dd/yyyy', 'en-US');
      }
    }
    const dayFirst = /^(\d{2})-(\d{2})-(\d{4})(?:[T\s].*)?$/.exec(trimmed);
    const iso = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/.exec(trimmed);
    const year = Number(dayFirst?.[3] ?? iso?.[1]);
    const month = Number(dayFirst?.[2] ?? iso?.[2]);
    const day = Number(dayFirst?.[1] ?? iso?.[3]);
    const date = new Date(0);
    date.setFullYear(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      console.warn('Unrecognized target install date returned by GetCustomerAccounts:', value);
      return value;
    }
    return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${String(year).padStart(4, '0')}`;
  }

  private getDeviceDisplayName(record: CustomerAccountRecord): string {
    const deviceTypeName = record.deviceTypeName ?? '';
    return record.deviceId === 1 && deviceTypeName.trim().toLowerCase() === 'mini' ? 'Max-Mini' : deviceTypeName;
  }

  private getDeviceTypeKey(record: CustomerAccountRecord): string | null {
    const deviceTypeName = record.deviceTypeName?.trim() ?? '';
    if (record.deviceId !== 1) {
      return null;
    }

    const deviceTypeId = [record.deviceTypeId, record.deviceType?.deviceTypeId, record.deviceType?.subDeviceTypeId]
      .find(id => typeof id === 'number' && Number.isInteger(id) && id > 0);
    if (deviceTypeId !== undefined) {
      return `device-type-id:${deviceTypeId}`;
    }

    return deviceTypeName ? `device-type-name:${deviceTypeName.toLocaleLowerCase()}` : null;
  }

  private updateVisibleRows(): void {
    const rows: LandingRow[] = [];
    for (const account of this.accounts) {
      let accountRow = this.accountRows.get(account);
      if (!accountRow) {
        accountRow = { type: 'account', account };
        this.accountRows.set(account, accountRow);
      }
      rows.push(accountRow);
      if (account.expanded) {
        for (const record of account.records) {
          let implementationRow = this.implementationRows.get(record);
          if (!implementationRow) {
            implementationRow = { type: 'implementation', account, record };
            this.implementationRows.set(record, implementationRow);
          }
          rows.push(implementationRow);
        }
      }
    }
    this.dataSource.data = rows;
  }

  trackRow(_index: number, row: LandingRow): string {
    return JSON.stringify([row.type, row.account.key, row.record?.customerId]);
  }

  isAccountRow(_index: number, row: LandingRow): boolean {
    return row.type === 'account';
  }

  isImplementationRow(_index: number, row: LandingRow): boolean {
    return row.type === 'implementation';
  }

  toggleAccount(account: CustomerAccountGroup): void {
    account.expanded = !account.expanded;
    this.updateVisibleRows();
  }

  getActionRecord(row: LandingRow): ImplementationRecord {
    return row.record ?? row.account.latestRecord;
  }

  canOpenAccountActions(account: CustomerAccountGroup): boolean {
    return account.deviceCount === 1
      || account.records.some(record => this.isImplementationSelected(record));
  }

  isImplementationSelected(record: ImplementationRecord): boolean {
    return this.selectedImplementationIds.has(record.customerId);
  }

  setImplementationSelected(record: ImplementationRecord, selected: boolean): void {
    if (selected) {
      const canAddToCurrentSelection = record.selectionDeviceTypeKey !== null
        && this.selectedCustomerKey === record.selectionCustomerKey
        && this.selectedDeviceTypeKey === record.selectionDeviceTypeKey;

      if (!canAddToCurrentSelection) {
        this.selectedImplementationIds.clear();
      }
      this.selectedImplementationIds.add(record.customerId);
      this.selectedCustomerKey = record.selectionCustomerKey;
      this.selectedDeviceTypeKey = record.selectionDeviceTypeKey;
      return;
    }
    this.selectedImplementationIds.delete(record.customerId);
    if (this.selectedImplementationIds.size === 0) {
      this.selectedCustomerKey = null;
      this.selectedDeviceTypeKey = null;
    }
  }

  getStatusClass(status: string): string {
    const normalizedStatus = status?.replace(/[\s_-]/g, '').toLowerCase() ?? '';

    if (normalizedStatus === 'imported') {
      return 'status-imported';
    }
    if (normalizedStatus === 'exported') {
      return 'status-exported';
    }
    if (/(fail|error|reject|cancel)/.test(normalizedStatus)) {
      return 'status-error';
    }
    if (/(process|pending|queue|progress|scheduled)/.test(normalizedStatus)) {
      return 'status-progress';
    }
    return 'status-neutral';
  }

  getFileStatusData(){
    const FILESTATUS_KEY = 'Filestatuskey'
    this.ngxLoader.start(FILESTATUS_KEY);
    this.imtoolService.getfileStatus().subscribe((result)=>{
    this.projectStatus = result.data;
    this.ngxLoader.stop(FILESTATUS_KEY);
    },
        (error: HttpErrorResponse) => {
          this.ngxLoader.stop(FILESTATUS_KEY);
          console.log(error);
          this.toastService.openToast(
            error.error.message ?? error.message,
            constants.ToastPanelType.error,
            2
          );
        })
  }


  getSalesforceStatusLabel(value: string | undefined): string {
    if (!value) {
      return '';
    }
    return this.salesforceStatusList.find(status => String(status.value) === value)?.key ?? value;
  }

  getSalesForceStatus(){
    const SALESFORCESTATUS_KEY = 'Salesforcesstatuskey'
    this.ngxLoader.start(SALESFORCESTATUS_KEY);
    this.imtoolService.getSalesForceStatus().subscribe((result)=>{
    this.salesforceStatusList = result.data;
    this.ngxLoader.stop(SALESFORCESTATUS_KEY);
    },
        (error: HttpErrorResponse) => {
          this.ngxLoader.stop(SALESFORCESTATUS_KEY);
          console.log(error);
          this.toastService.openToast(
            error.error.message ?? error.message,
            constants.ToastPanelType.error,
            2
          );
        })
  }
  public doFilter = (value: any, isTextSearch:boolean, product:string = '') => {
    if(isTextSearch)
    {
     this.searchText = value;
    }
    else{
    this.statusId = value;
    this.selectedProjectStatus = value;
    }
    this.selectedProduct = product;
    this.currentPage = 0;
    this.getCustomerData();
  };

  openAlertWindow(row: any) {
    const dialogRef = this.dialog.open(AlertWizardComponent, {
      backdropClass: ['smDialog'],
      disableClose: true,
      data: {
        alertIcon: "/assets/img/icons/ico.16.warning.svg",
        title: "Warning",
        bodyMessage: "Are you sure you want to remove?",
        bodyMessage2: "",
        cancelBtnText: "Go Back",
        confirmBtnText: "Continue",
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result.data) {
        this.customerRemove(row.customerId);
      } else {
        console.log("Cancle", result);
      }
    });
  }

  customerRemove(customerId: string) {
    const CUSTOMERREMOVE_KEY = 'Customeremovekey'
    this.ngxLoader.start(CUSTOMERREMOVE_KEY);
    this.imtoolService.customerRemove(customerId).subscribe(
      (result) => {
        this.ngxLoader.stop(CUSTOMERREMOVE_KEY);
        if (result.statusCode == 200) {
          this.toastService.openToast(
            result.message,
            constants.ToastPanelType.done,
            2
          );
          this.getCustomerData();
        } else {
          this.toastService.openToast(
            result.message,
            constants.ToastPanelType.error,
            2
          );
            this.ngxLoader.stop(CUSTOMERREMOVE_KEY)
        }
      },
      (error: HttpErrorResponse) => {
        this.ngxLoader.stop(CUSTOMERREMOVE_KEY);
        console.log(error);
        this.toastService.openToast(
          error.error.message ?? error.message,
          constants.ToastPanelType.error,
          2
        );
      }
    );
  }
  openSelectedImplementation(): void {
    if (this.selectedImplementationIds.size === 1) {
      const account = this.accounts.find(item => item.key === this.selectedCustomerKey);
      const record = account?.records.find(item => this.isImplementationSelected(item));
      if (record) {
        this.openAddSite(record);
      }
    }
  }

  openAddSite(row: any) {
    const dialogSiteRef = this.dialog.open(ImportWizardComponent, {
      backdropClass: ['lgDialog'],
      disableClose: true,
      data: { customer: row },
    });
    dialogSiteRef.afterClosed().subscribe((result) => {
      this.getCustomerData();
    });
  }
  canExport(row: CustomerInfo): boolean {
    return [3, 4, 5, 6].includes(row?.status?.id);
  }

  redirectPageBasedOnStatus(row: CustomerInfo) {
    if (row.status.id === 3 || row.status.id===5 || row.status.id === 4 || row.status.id === 6) {
      if(row?.deviceId===3){
        this.route.navigate(["/im-tool/atp-export", row.customerId]);
      }
      else if(row?.deviceId===2){
        this.route.navigate(["/im-tool/pass-export", row.customerId]);
      }
      else if(row?.deviceId===4){
        this.route.navigate(["/im-tool/synmed-export", row.customerId]);
      }
      else if(row?.deviceId===5){
        this.route.navigate(["/im-tool/mini-export", row.customerId]);
      }
      else if(row?.deviceId===6){
        this.route.navigate(["/im-tool/atp-caps-export", row.customerId]);
      }
      else if(row?.deviceId===7){
        this.route.navigate(["/im-tool/legacy-connect", row.customerId]);
      }
      else{
        this.route.navigate(["/im-tool/export", row.customerId]);
      }

    } else if (row.status.id === 1 || row.status.id === 2) {
      this.openAddSite(row);
    }
  }




}
